import { defineStore } from 'pinia';
import { toRaw } from 'vue';
import type { ComponentDto } from '@/ApiClient';
import { useApi } from '@lib/api';
import { useUser } from '@/store/user';
import { signalrListen } from '@lib/signalr';
import { CACHE_TTL, onTabResume } from '@/store/cache-ttl';
import { entityCache, keyedLruCache, pruneIds } from '@/store/cache-primitives';

export interface ComponentTableCache {
  filterKey: string;
  orderedIds: number[];
  total: number;
}

// Table caches are keyed per filter combination so switching filters back and forth
// restores instantly.
const MAX_TABLE_CACHES = 20;

export const useComponents = defineStore('component-cache', () => {
  // Resolved once here rather than per call: `useApi` is an `inject`, so it only
  // answers while a component is setting up. Read from inside `ensureMany` or
  // the SignalR handler it came back undefined and the next line threw.
  const api = useApi();

  const entities = entityCache<ComponentDto>();
  const components = entities.data;
  const lru = keyedLruCache<ComponentTableCache>(MAX_TABLE_CACHES);
  const tableCaches = lru.data;

  // Snapshot persistence contract — see store/warm-boot.ts. The components page's
  // reload() always refetches after seeding from these caches, so disk-seeded (possibly
  // stale) table caches revalidate on table mount without extra machinery.
  const snapshotDto = 'ComponentDto' as const;
  // ComponentTableCache is a frontend-defined shape the DTO hash can't guard — bump
  // this whenever the snapshot/ComponentTableCache shape changes.
  const SNAPSHOT_VERSION = 2;

  type ComponentsSnapshot = {
    version: number;
    components: Record<number, ComponentDto>;
    tableCaches: Record<string, ComponentTableCache>;
  };

  function snapshotData(): ComponentsSnapshot {
    // toRaw: IndexedDB's structured clone can't handle Vue's reactive proxies
    return { version: SNAPSHOT_VERSION, components: toRaw(components.value), tableCaches: toRaw(tableCaches.value) };
  }

  function seedSnapshot(data: unknown) {
    const snapshot = data as Partial<ComponentsSnapshot>;
    if (snapshot.version !== SNAPSHOT_VERSION) return;
    if (Object.keys(components.value).length > 0 || Object.keys(tableCaches.value).length > 0) return;
    entities.seed(snapshot.components ?? {});
    tableCaches.value = snapshot.tableCaches ?? {};
  }

  function getOrdered(filterKey: string): ComponentDto[] {
    const cache = lru.get(filterKey);
    if (!cache) return [];
    return cache.orderedIds.map((id) => components.value[id]).filter(Boolean);
  }

  function setTableState(filterKey: string, ids: number[], total: number) {
    lru.set(filterKey, { filterKey, orderedIds: ids, total });
  }

  function appendToTableState(filterKey: string, ids: number[]) {
    const cache = lru.get(filterKey);
    if (!cache) return;
    cache.orderedIds = [...cache.orderedIds, ...ids];
  }

  // Fetch component IDs that aren't already cached. Used by surfaces that
  // need the rich ComponentDto fields (e.g. componentTagIds) for a list of
  // BomComponentDtos that only carry the slim shape.
  //
  // `{ force: true }` re-fetches even already-cached ids — useful when the
  // cache might be stale (e.g. tags added in another tab / page).
  const pending = new Map<number, Promise<ComponentDto | undefined>>();
  async function ensureMany(ids: number[], opts: { force?: boolean } = {}): Promise<void> {
    const brandId = useUser().brandId;
    const targets = ids.filter((id) => {
      if (id == null) return false;
      if (pending.has(id)) return false;
      if (!opts.force && components.value[id]) return false;
      return true;
    });
    if (!targets.length) return;
    const promises = targets.map((id) => {
      const p = api
        .component_GetComponent(brandId, id)
        .then((c) => {
          if (c) components.value[c.id] = c;
          pending.delete(id);
          return c;
        })
        .catch(() => {
          pending.delete(id);
          return undefined;
        });
      pending.set(id, p);
      return p;
    });
    await Promise.all(promises);
  }

  function remove(ids: number[]) {
    entities.remove(ids);
    pruneIds(tableCaches, ids);
  }

  function clear() {
    entities.clear();
    lru.clear();
  }

  function listen() {
    signalrListen(
      'ComponentsUpdated',
      async (componentIds: number[]) => {
        const brandId = useUser().brandId;

        for (const componentId of componentIds) {
          if (!components.value[componentId]) continue;

          const component = await api.component_GetComponent(brandId, componentId);
          entities.set(component);
        }
      },
      { listenForever: true }
    );

    signalrListen(
      'ComponentsDeleted',
      (componentIds: number[]) => {
        remove(componentIds);
      },
      { listenForever: true }
    );
  }

  onTabResume((elapsed) => {
    // Drop only the list/scroll caches so the next table mount fetches a fresh list;
    // keep the component DTOs so anything rendered shows data instead of skeletons.
    if (elapsed >= CACHE_TTL) lru.clear();
  });

  return {
    components,
    tableCaches,
    getTableCache: lru.get,
    snapshotDto,
    snapshotData,
    seedSnapshot,
    get: entities.get,
    getOrdered,
    cache: entities.cache,
    setTableState,
    appendToTableState,
    update: entities.set,
    ensureMany,
    remove,
    clear,
    listen,
  };
});
